import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, ErrorState, Icon, LoadingState, PageContainer, SectionHeader } from "../components";
import { useAsync } from "../general/useAsync";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import * as Management from "../management/ManagementStore";
import * as Store from "./MarkGridStore";
import { DistributionChart, DivisionAnalysisChart, PassRateGauge } from "./Charts";
import { NewAssessmentModal, NewDivisionModal } from "./Dialogs";
import { TileBar, groupRoute, sheetRoute, useClassFilter } from "./Shared";
import { Tabs } from "../components/Tabs";
import { SubjectComments } from "./SubjectComments";

/**
 * A module, or one division inside it, as tiles: each child division with its share of the calculated mark, the
 * assessments directly inside a division, and the subject's analytics underneath.
 *
 * Weights are read off the calculation edges the module's procedures generated. Calculation coverage diagnostics
 * belong in findings; capture tiles show the available structure and stored results.
 */
const ModuleOverview: React.FC = () => {
    const { moduleID = "", groupID } = useParams();
    const [classID, , query] = useClassFilter();
    const canManage = usePermission(Permissions.ACTIVITIES_WRITE);
    const navigate = useNavigate();

    const overview = useAsync(() => Store.getOverview(moduleID, groupID, classID || undefined), [moduleID, groupID, classID]);
    const teachingGroups = useAsync(() => Management.listTeachingGroups(), [], { initial: [] });
    const [adding, setAdding] = React.useState<"division" | "assessment" | null>(null);
    // Kept across term switches: the route element stays mounted, so a teacher writing comments stays on Comments.
    const [tab, setTab] = React.useState<"overview" | "comments">("overview");

    const groups = React.useMemo(
        () => (teachingGroups.data ?? []).filter((g) => g.qualificationModuleID === moduleID).map((g) => ({ id: g.id, name: g.name })),
        [teachingGroups.data, moduleID]
    );

    if (overview.isLoading && !overview.data) return <PageContainer><LoadingState /></PageContainer>;
    if (overview.error) return <PageContainer><ErrorState message={overview.error} onRetry={overview.refresh} /></PageContainer>;
    const data = overview.data;
    if (!data) return null;

    const divisions = (data.children ?? []).filter((c) => c.entityType === "AssessmentGroup");
    const assessments = (data.children ?? []).filter((c) => c.entityType === "Assessment");
    const title = data.groupName ? `${data.groupName}` : data.moduleName;
    // The term is in the switcher, so the subject tile only carries the grade.
    const detail = data.levelName ?? undefined;
    const showComments = !groupID && tab === "comments";

    /** A division with sub-divisions opens as tiles; a division of assessments opens straight into its mark sheet. */
    const divisionLink = (d: Store.MarkGridNodeModel) =>
        (d.groupCount ?? 0) > 0 ? groupRoute(moduleID, d.id ?? "", query) : sheetRoute(moduleID, d.id, query);

    return (
        <PageContainer>
            <TileBar
                moduleID={moduleID}
                moduleName={data.moduleName ?? ""}
                moduleDetail={detail}
                path={data.path ?? []}
                teachingGroups={groups}
                terms={data.terms ?? []}
                current={groupID ? "group" : "module"}
            />

            <div className="mb-4 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                <div className="min-w-0">
                    <h1 className="text-xl font-semibold text-text-strong">{title}</h1>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge tone="neutral">
                            {data.studentCount} student{data.studentCount === 1 ? "" : "s"}
                        </Badge>
                        {data.resultMarkTypeName && <Badge tone="info">{data.resultMarkTypeName}</Badge>}
                        {data.average !== undefined && data.average !== null && (
                            <Badge tone="primary">Average {Store.formatMark(data.average)}%</Badge>
                        )}
                        {classID && <Badge tone="neutral">One class</Badge>}
                    </div>
                </div>
                <Button icon={["fad", "table-list"]} onClick={() => navigate(sheetRoute(moduleID, groupID, query))}>
                    {groupID ? "Open mark sheet" : "All marks"}
                </Button>
            </div>

            {!groupID && (
                <Tabs
                    tabs={[
                        { key: "overview", label: "Overview", icon: ["fad", "chart-simple"] },
                        { key: "comments", label: "Comments", icon: ["fad", "comment"] },
                    ]}
                    active={tab}
                    onChange={(key) => setTab(key as "overview" | "comments")}
                />
            )}

            {showComments ? (
                <SubjectComments
                    key={`${moduleID}|${classID}`}
                    moduleID={moduleID}
                    classID={classID || undefined}
                    termName={(data.terms ?? []).find((t) => t.isViewing)?.cycleName ?? data.cycleName ?? undefined}
                />
            ) : (
                <>
                    <section className="mb-8">
                        <SectionHeader
                            title={groupID ? "Inside this division" : "Divisions"}
                            description="Open a division to enter its assessment marks."
                        />
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-3">
                            {divisions.map((d) => (
                                <NodeTile
                                    key={d.id}
                                    to={divisionLink(d)}
                                    node={d}
                                    footer={d.counted ? `Weight ${Store.formatMark(d.weightPercent)}%` : `${d.assessmentCount ?? 0} assessment${d.assessmentCount === 1 ? "" : "s"}`}
                                    weight={d.counted ? d.weightPercent : undefined}
                                />
                            ))}
                            {assessments.map((a) => (
                                <NodeTile
                                    key={a.id}
                                    to={sheetRoute(moduleID, groupID, query)}
                                    node={a}
                                    footer={`Out of ${Store.formatMark(a.total)}`}
                                    subtle
                                />
                            ))}
                            {canManage && (
                                <AddTile icon={["fad", "folder-plus"]} label="New division" onClick={() => setAdding("division")} />
                            )}
                            {canManage && groupID && (
                                <AddTile icon={["fad", "file-circle-plus"]} label="New assessment" onClick={() => setAdding("assessment")} />
                            )}
                        </div>
                        {!divisions.length && !assessments.length && !canManage && (
                            <Card>
                                <EmptyState icon={["fad", "sitemap"]} title="Nothing here yet" hint="This subject has no divisions or assessments set up." />
                            </Card>
                        )}
                    </section>

                    <section>
                        <SectionHeader
                            title="Analytics"
                            description={`From the stored ${data.resultMarkTypeName ?? "results"}${classID ? ", for the selected class" : ""}.`}
                        />
                        <div className="grid gap-4 lg:grid-cols-3">
                            <ChartCard title="Pass rate">
                                <PassRateGauge passRate={data.passRate} passMark={data.passMark} passMarkIsPercent={data.passMarkIsPercent} />
                            </ChartCard>
                            <ChartCard title="Marks distribution">
                                <DistributionChart buckets={data.distribution ?? []} label={data.resultMarkTypeName ?? "Results"} />
                            </ChartCard>
                            <ChartCard title={groupID ? "Assessment analysis" : "Division analysis"}>
                                <DivisionAnalysisChart
                                    bars={(data.children ?? []).map((c) => ({
                                        name: c.name ?? "",
                                        average: c.average,
                                        standardDeviation: c.standardDeviation,
                                    }))}
                                    overallAverage={data.average}
                                />
                            </ChartCard>
                        </div>
                    </section>
                </>
            )}

            <NewDivisionModal
                isOpen={adding === "division"}
                onClose={() => setAdding(null)}
                moduleID={moduleID}
                parentGroupID={groupID}
                parentName={title ?? ""}
                onCreated={overview.refresh}
            />
            {groupID && (
                <NewAssessmentModal
                    isOpen={adding === "assessment"}
                    onClose={() => setAdding(null)}
                    groupID={groupID}
                    groupName={title ?? ""}
                    onCreated={overview.refresh}
                />
            )}
        </PageContainer>
    );
};

const square = "flex min-h-28 flex-col justify-between rounded-md p-3 text-sm shadow-sm transition-[background-color,box-shadow] hover:shadow-md";

const NodeTile: React.FC<{ to: string; node: Store.MarkGridNodeModel; footer: string; subtle?: boolean; weight?: number | null }> = ({
    to,
    node,
    footer,
    subtle,
    weight,
}) => {
    const counted = node.entityType === "AssessmentGroup" || node.counted !== false;
    const tone = !counted
        ? "border border-dashed border-border-strong bg-surface-muted text-text shadow-none hover:bg-surface-hover"
        : subtle
          ? "bg-primary-soft text-primary-text ring-1 ring-primary-tint hover:bg-primary-tint"
          : "bg-primary text-on-primary hover:bg-primary-hover";

    return (
        <Link to={to} className={`${square} ${tone}`} title={node.typeName ? `${node.name} (${node.typeName})` : node.name}>
            <span className="line-clamp-2 font-medium leading-snug">{node.name}</span>
            <span className="flex flex-col gap-1.5">
                <span className="flex items-end justify-between gap-1 text-xs">
                    <span>{footer}</span>
                    {!counted && <Badge tone="warning">0%</Badge>}
                    {counted && node.average !== undefined && node.average !== null && (
                        <span className="opacity-80" title="Average of stored results">
                            x̄ {Store.formatMark(node.average)}
                        </span>
                    )}
                </span>
                {weight !== undefined && weight !== null && (
                    <span className="block h-1 overflow-hidden rounded-full bg-on-primary/25" aria-hidden>
                        <span className="block h-full rounded-full bg-on-primary/80" style={{ width: `${Math.min(100, Math.max(0, weight))}%` }} />
                    </span>
                )}
            </span>
        </Link>
    );
};

const AddTile: React.FC<{ icon: any; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex min-h-28 flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-primary text-sm text-primary-text transition-colors hover:bg-primary-soft"
    >
        <Icon icon={icon} className="text-xl" />
        <span className="font-medium">{label}</span>
    </button>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Card>
        <CardHeader className="!px-4 !py-2.5">
            <h3 className="text-sm font-medium text-text-strong">{title}</h3>
        </CardHeader>
        <CardBody className="p-4">{children}</CardBody>
    </Card>
);

export default ModuleOverview;
